/**
 * LanguagesPanel — módulo de idiomas del panel de plataforma.
 *
 * Permite:
 *   · Sincronizar los catálogos que viajan en el build (es, ca, en).
 *   · Ver la cobertura de cada idioma frente al español.
 *   · Añadir idiomas nuevos (código BCP-47 + nombre nativo).
 *   · Autotraducir las claves que falten en tandas, con progreso.
 *   · Revisar/editar a mano las traducciones (las manuales nunca se pisan).
 *
 * El español es la fuente de verdad. El backend no necesita el JSON del build:
 * se lo envía esta pantalla al pulsar «Sincronizar catálogos incluidos».
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { BUNDLED_LANGUAGES } from '../i18n/config';
import { languageBadge } from '../i18n/languages';
import './LanguagesPanel.css';

const BUILTIN_CODES = Object.keys(BUNDLED_LANGUAGES);

function CoverageBar({ coverage }) {
  const total = coverage?.total || 0;
  const translated = coverage?.translated || 0;
  const pct = total ? Math.round((translated / total) * 100) : 0;
  return (
    <div className="lang-coverage">
      <div className="lang-coverage-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <div className="lang-coverage-text">
        <strong>{pct}%</strong>
        <span>
          {translated}/{total || '—'} · {coverage?.missing || 0} pendientes
        </span>
      </div>
    </div>
  );
}

function TranslationReview({ language, onClose, onSaved }) {
  const [entries, setEntries] = useState({});
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getI18nBundle(language.code)
      .then((data) => {
        if (cancelled) return;
        setEntries(data.entries || {});
        setDraft({});
        setError('');
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'No se pudo cargar el catálogo');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [language.code]);

  const rows = useMemo(() => {
    const merged = { ...entries, ...draft };
    const needle = search.trim().toLowerCase();
    return Object.entries(merged)
      .filter(([key, value]) => {
        if (!needle) return true;
        return key.toLowerCase().includes(needle) || String(value).toLowerCase().includes(needle);
      })
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 250);
  }, [entries, draft, search]);

  const changedCount = Object.keys(draft).length;

  const save = async () => {
    if (!changedCount) return;
    setSaving(true);
    setError('');
    try {
      await api.setI18nTranslations(language.code, draft);
      setEntries((prev) => ({ ...prev, ...draft }));
      setDraft({});
      onSaved();
    } catch (err) {
      setError(err.message || 'No se pudieron guardar las traducciones');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lang-modal-overlay" role="dialog" aria-modal="true">
      <div className="lang-modal">
        <header className="lang-modal-head">
          <div>
            <span className="lang-code">{languageBadge(language.code)}</span>
            <h3>Revisar traducciones · {language.name}</h3>
            <p>
              Las ediciones se guardan como <strong>manual</strong> y no se
              sobrescriben al volver a autotraducir.
            </p>
          </div>
          <button type="button" className="lang-btn" onClick={onClose}>Cerrar</button>
        </header>

        <div className="lang-modal-tools">
          <input
            type="search"
            className="lang-input"
            placeholder="Buscar por clave o texto…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <span className="lang-modal-count">{rows.length} claves visibles</span>
          <button
            type="button"
            className="lang-btn lang-btn--primary"
            onClick={save}
            disabled={saving || !changedCount}
          >
            {saving ? 'Guardando…' : `Guardar cambios${changedCount ? ` (${changedCount})` : ''}`}
          </button>
        </div>

        {error && <div className="lang-alert lang-alert--error">{error}</div>}

        {loading ? (
          <div className="lang-modal-loading">Cargando catálogo…</div>
        ) : (
          <div className="lang-review-list">
            {rows.map(([key, value]) => (
              <label className="lang-review-row" key={key}>
                <code className="lang-review-key">{key}</code>
                <textarea
                  className="lang-input lang-review-value"
                  rows={value.length > 90 ? 3 : 1}
                  value={draft[key] ?? value}
                  onChange={(event) => {
                    const next = event.target.value;
                    setDraft((prev) => {
                      const copy = { ...prev };
                      if (next === entries[key]) delete copy[key];
                      else copy[key] = next;
                      return copy;
                    });
                  }}
                />
              </label>
            ))}
            {rows.length === 0 && <div className="lang-modal-loading">No hay coincidencias.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function LanguagesPanel() {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [provider, setProvider] = useState('google');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyCode, setBusyCode] = useState(null);
  const [progress, setProgress] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', nameEs: '', autoTranslate: true });
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getI18nLanguages();
      setLanguages(data.languages || []);
      setProvider(data.provider || 'google');
      setError('');
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los idiomas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const syncBundled = async () => {
    setSyncing(true);
    setNotice('');
    setError('');
    try {
      const summary = [];
      for (const code of BUILTIN_CODES) {
        const catalog = BUNDLED_LANGUAGES[code];
        if (!catalog) continue;
        const result = await api.syncI18nCatalog(code, catalog, { replace: true });
        summary.push(`${code}: ${result.received} claves`);
      }
      setNotice(`Catálogos sincronizados · ${summary.join(' · ')}`);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudieron sincronizar los catálogos');
    } finally {
      setSyncing(false);
    }
  };

  const ensureBaseCatalog = async () => {
    const spanish = languages.find((item) => item.code === 'es');
    if (spanish?.coverage?.total) return;
    await api.syncI18nCatalog('es', BUNDLED_LANGUAGES.es, { replace: true });
  };

  const autoTranslate = async (language) => {
    setBusyCode(language.code);
    setError('');
    setNotice('');
    const total = language.coverage?.missing || 0;
    setProgress((prev) => ({
      ...prev,
      [language.code]: { done: 0, total, error: '' },
    }));

    try {
      await ensureBaseCatalog();
      let remaining = total;
      let guard = 0;
      while (guard < 250) {
        guard += 1;
        const result = await api.translateI18nLanguage(language.code, { limit: 40 });
        const currentRemaining = result.remaining;
        remaining = currentRemaining;
        setProgress((prev) => ({
          ...prev,
          [language.code]: {
            done: Math.max(0, total - currentRemaining),
            total,
            error: '',
          },
        }));
        if (result.done || currentRemaining <= 0) break;
      }
      setNotice(
        `Autotraducción de ${language.name} terminada` +
          (remaining > 0 ? ` · quedan ${remaining} claves` : '')
      );
      await refresh();
    } catch (err) {
      setProgress((prev) => ({
        ...prev,
        [language.code]: {
          ...(prev[language.code] || {}),
          error: err.message || 'Fallo de autotraducción',
        },
      }));
      setError(err.message || 'No se pudo autotraducir');
    } finally {
      setBusyCode(null);
    }
  };

  const toggleEnabled = async (language) => {
    if (language.is_default) return;
    try {
      await api.updateI18nLanguage(language.code, { enabled: !language.enabled });
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado');
    }
  };

  const createLanguage = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    setNotice('');
    try {
      await api.createI18nLanguage({
        code: form.code.trim().toLowerCase(),
        name: form.name.trim(),
        nameEs: form.nameEs.trim() || null,
        autoTranslate: form.autoTranslate,
      });
      setForm({ code: '', name: '', nameEs: '', autoTranslate: true });
      setNotice(`Idioma ${form.code.trim().toLowerCase()} añadido. Pulsa «Autotraducir» para completarlo.`);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo añadir el idioma');
    } finally {
      setCreating(false);
    }
  };

  const removeLanguage = async (language) => {
    const ok = window.confirm(
      `¿Eliminar el idioma ${language.name} (${language.code}) y todas sus traducciones?`
    );
    if (!ok) return;
    try {
      await api.deleteI18nLanguage(language.code);
      setNotice(`Idioma ${language.code} eliminado.`);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el idioma');
    }
  };

  if (loading) {
    return <div className="lang-panel-loading">Cargando idiomas…</div>;
  }

  return (
    <section className="lang-panel">
      <header className="lang-panel-head">
        <div>
          <h3 className="lang-panel-title">{t('languages.title', 'Idiomas de la CMDB')}</h3>
          <p className="lang-panel-subtitle">
            El español es el catálogo base. Los idiomas nuevos se completan con
            autotraducción y se pueden corregir a mano. El idioma de cada usuario
            se guarda en su perfil y se pregunta la primera vez que entra.
          </p>
          <p className="lang-panel-provider">
            Proveedor de autotraducción: <code>{provider}</code>
            {' · '}configurable con <code>I18N_TRANSLATE_PROVIDER</code>
          </p>
        </div>
        <button
          type="button"
          className="lang-btn"
          onClick={syncBundled}
          disabled={syncing}
        >
          {syncing ? 'Sincronizando…' : 'Sincronizar catálogos incluidos'}
        </button>
      </header>

      {error && <div className="lang-alert lang-alert--error">{error}</div>}
      {notice && <div className="lang-alert lang-alert--ok">{notice}</div>}

      <div className="lang-list">
        {languages.map((language) => {
          const builtin = BUILTIN_CODES.includes(language.code);
          const isBusy = busyCode === language.code;
          const prog = progress[language.code];
          const missing = language.coverage?.missing || 0;
          return (
            <article className={`lang-card ${language.enabled ? '' : 'is-disabled'}`} key={language.code}>
              <div className="lang-card-head">
                <span className="lang-code">{languageBadge(language.code)}</span>
                <div className="lang-card-name">
                  <strong>{language.name}</strong>
                  <span>
                    {language.name_es && language.name_es !== language.name
                      ? language.name_es
                      : language.code}
                    {language.is_default ? ' · idioma base' : ''}
                  </span>
                </div>
                <span className={`lang-tag ${builtin ? 'is-builtin' : ''}`}>
                  {builtin ? 'incluido en el build' : 'servidor'}
                </span>
                {!language.is_default && (
                  <button
                    type="button"
                    className={`lang-toggle ${language.enabled ? 'is-on' : ''}`}
                    onClick={() => toggleEnabled(language)}
                    title={language.enabled ? 'Desactivar idioma' : 'Activar idioma'}
                  >
                    {language.enabled ? 'Activo' : 'Inactivo'}
                  </button>
                )}
              </div>

              <CoverageBar coverage={language.coverage} />

              <div className="lang-card-meta">
                <span>{language.coverage?.auto || 0} automáticas</span>
                <span>{language.coverage?.manual || 0} manuales</span>
                {language.updated_at && (
                  <span>actualizado {new Date(language.updated_at).toLocaleString()}</span>
                )}
              </div>

              <div className="lang-card-actions">
                <button
                  type="button"
                  className="lang-btn lang-btn--primary"
                  onClick={() => autoTranslate(language)}
                  disabled={isBusy || language.code === 'es' || missing === 0}
                >
                  {isBusy
                    ? 'Traduciendo…'
                    : missing > 0
                      ? `Autotraducir ${missing} claves`
                      : 'Completo'}
                </button>
                <button type="button" className="lang-btn" onClick={() => setReviewing(language)}>
                  Revisar / editar
                </button>
                {!language.is_default && (
                  <button
                    type="button"
                    className="lang-btn lang-btn--danger"
                    onClick={() => removeLanguage(language)}
                    disabled={isBusy}
                  >
                    Eliminar
                  </button>
                )}
              </div>

              {prog && (isBusy || prog.error) && (
                <div className={`lang-progress ${prog.error ? 'is-error' : ''}`}>
                  {prog.error ? (
                    <span>{prog.error}</span>
                  ) : (
                    <>
                      <div className="lang-progress-bar">
                        <span
                          style={{
                            width: `${
                              prog.total ? Math.round((prog.done / prog.total) * 100) : 0
                            }%`,
                          }}
                        />
                      </div>
                      <span>
                        {prog.done}/{prog.total} traducidas
                      </span>
                    </>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <form className="lang-add" onSubmit={createLanguage}>
        <div className="lang-add-head">
          <h4>Añadir idioma</h4>
          <p>
            Código BCP-47 (<code>pt</code>, <code>pt-br</code>, <code>fr</code>…) y
            nombre nativo. Después se autotraduce desde el español.
          </p>
        </div>
        <div className="lang-add-fields">
          <input
            className="lang-input"
            placeholder="Código (ej. pt)"
            value={form.code}
            onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
            required
            pattern="[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})?"
          />
          <input
            className="lang-input"
            placeholder="Nombre nativo (ej. Português)"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
          <input
            className="lang-input"
            placeholder="Nombre en español (opcional)"
            value={form.nameEs}
            onChange={(event) => setForm((prev) => ({ ...prev, nameEs: event.target.value }))}
          />
          <label className="lang-check">
            <input
              type="checkbox"
              checked={form.autoTranslate}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, autoTranslate: event.target.checked }))
              }
            />
            Autotraducir desde el español
          </label>
          <button type="submit" className="lang-btn lang-btn--primary" disabled={creating}>
            {creating ? 'Añadiendo…' : 'Añadir idioma'}
          </button>
        </div>
      </form>

      {reviewing && (
        <TranslationReview
          language={reviewing}
          onClose={() => setReviewing(null)}
          onSaved={() => {
            setNotice(`Traducciones de ${reviewing.name} guardadas.`);
            refresh();
          }}
        />
      )}
    </section>
  );
}

export default LanguagesPanel;
